import { addMarketplaceEntities, getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { types as sdkTypes, createImageVariantConfig } from '../../util/sdkLoader';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { hasQueryParam } from '../../util/urlHelpers';
import { filterCompletedListings } from '../../util/listingsHelpers';
import {
  getSubcategoriesForCategory,
  normalizeLegacySubcategorySlug,
  STORE_SIDEBAR_CATEGORIES,
} from '../../util/storeTaxonomy';
import { fetchCreators, fetchStoresWithReviews } from '../../ducks/user.duck';
import { mapTaxonomySearchParamsToSdkQuery } from '../SearchPage/SearchPage.shared';

const { UUID } = sdkTypes;

// ================ Action types ================ //

export const SET_INITIAL_STATE = 'app/TeamPage/SET_INITIAL_STATE';

export const SHOW_USER_REQUEST = 'app/TeamPage/SHOW_USER_REQUEST';
export const SHOW_USER_SUCCESS = 'app/TeamPage/SHOW_USER_SUCCESS';
export const SHOW_USER_ERROR = 'app/TeamPage/SHOW_USER_ERROR';

export const QUERY_LISTINGS_REQUEST = 'app/TeamPage/QUERY_LISTINGS_REQUEST';
export const QUERY_LISTINGS_SUCCESS = 'app/TeamPage/QUERY_LISTINGS_SUCCESS';
export const QUERY_LISTINGS_ERROR = 'app/TeamPage/QUERY_LISTINGS_ERROR';

export const QUERY_REVIEWS_REQUEST = 'app/TeamPage/QUERY_REVIEWS_REQUEST';
export const QUERY_REVIEWS_SUCCESS = 'app/TeamPage/QUERY_REVIEWS_SUCCESS';
export const QUERY_REVIEWS_ERROR = 'app/TeamPage/QUERY_REVIEWS_ERROR';

export const SET_CATEGORIES = 'app/TeamPage/SET_CATEGORIES';
export const SET_SUBCATEGORIES = 'app/TeamPage/SET_SUBCATEGORIES';
export const SET_CURRENT_CATEGORY = 'app/TeamPage/SET_CURRENT_CATEGORY';
export const SET_CURRENT_SUBCATEGORY = 'app/TeamPage/SET_CURRENT_SUBCATEGORY';

export const FETCH_MANUFACTURER_IMAGES_REQUEST = 'app/TeamPage/FETCH_MANUFACTURER_IMAGES_REQUEST';
export const FETCH_MANUFACTURER_IMAGES_SUCCESS = 'app/TeamPage/FETCH_MANUFACTURER_IMAGES_SUCCESS';
export const FETCH_MANUFACTURER_IMAGES_ERROR = 'app/TeamPage/FETCH_MANUFACTURER_IMAGES_ERROR';

// ================ Reducer ================ //

const initialState = {
  userId: null,
  userListingRefs: [],
  userShowError: null,
  // Owned by this page instead of read back from the shared entity store: the
  // embedded search merges the same seller without `profile.bio` and would drop it.
  profileBio: null,
  fetchingListings: true,
  queryListingsError: null,
  reviews: [],
  queryReviewsError: null,
  categories: [],
  subcategories: [],
  currentCategory: 'all',
  currentSubcategory: 'all',
  manufacturerImages: {},
  manufacturerImagesInProgress: false,
  manufacturerImagesError: null,
};

export default function TeamPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_STATE:
      return { ...initialState };
    case SHOW_USER_REQUEST:
      return { ...state, userShowError: null, userId: payload.userId };
    case SHOW_USER_SUCCESS:
      return { ...state, profileBio: payload };
    case SHOW_USER_ERROR:
      return { ...state, userShowError: payload };

    case QUERY_LISTINGS_REQUEST:
      return {
        ...state,
        fetchingListings: true,

        // Empty listings only when user id changes
        userListingRefs: payload.userId === state.userId ? state.userListingRefs : [],

        queryListingsError: null,
      };
    case QUERY_LISTINGS_SUCCESS:
      return { ...state, fetchingListings: false, userListingRefs: payload.listingRefs };
    case QUERY_LISTINGS_ERROR:
      return {
        ...state,
        fetchingListings: false,
        userListingRefs: [],
        queryListingsError: payload,
      };
    case QUERY_REVIEWS_REQUEST:
      return { ...state, queryReviewsError: null };
    case QUERY_REVIEWS_SUCCESS:
      return { ...state, reviews: payload };
    case QUERY_REVIEWS_ERROR:
      return { ...state, reviews: [], queryReviewsError: payload };

    case SET_CATEGORIES:
      return { ...state, categories: payload };
    case SET_SUBCATEGORIES:
      return { ...state, subcategories: payload };
    case SET_CURRENT_CATEGORY:
      return { ...state, currentCategory: payload };
    case SET_CURRENT_SUBCATEGORY:
      return { ...state, currentSubcategory: payload };

    case FETCH_MANUFACTURER_IMAGES_REQUEST:
      return { ...state, manufacturerImagesInProgress: true, manufacturerImagesError: null };
    case FETCH_MANUFACTURER_IMAGES_SUCCESS:
      return {
        ...state,
        manufacturerImagesInProgress: false,
        manufacturerImages: { ...state.manufacturerImages, ...payload },
      };
    case FETCH_MANUFACTURER_IMAGES_ERROR:
      return { ...state, manufacturerImagesInProgress: false, manufacturerImagesError: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //
export const setInitialState = () => ({
  type: SET_INITIAL_STATE,
});

export const showUserRequest = userId => ({
  type: SHOW_USER_REQUEST,
  payload: { userId },
});

export const showUserSuccess = bio => ({
  type: SHOW_USER_SUCCESS,
  payload: bio,
});

export const showUserError = e => ({
  type: SHOW_USER_ERROR,
  error: true,
  payload: e,
});

export const queryListingsRequest = userId => ({
  type: QUERY_LISTINGS_REQUEST,
  payload: { userId },
});

export const queryListingsSuccess = listingRefs => ({
  type: QUERY_LISTINGS_SUCCESS,
  payload: { listingRefs },
});

export const queryListingsError = e => ({
  type: QUERY_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const queryReviewsRequest = () => ({
  type: QUERY_REVIEWS_REQUEST,
});

export const queryReviewsSuccess = reviews => ({
  type: QUERY_REVIEWS_SUCCESS,
  payload: reviews,
});

export const queryReviewsError = e => ({
  type: QUERY_REVIEWS_ERROR,
  error: true,
  payload: e,
});

export const setCategories = e => ({
  type: SET_CATEGORIES,
  payload: e,
});

export const setSubcategories = e => ({
  type: SET_SUBCATEGORIES,
  payload: e,
});

export const setCurrentCategory = e => ({
  type: SET_CURRENT_CATEGORY,
  payload: e,
});

export const setCurrentSubcategory = e => ({
  type: SET_CURRENT_SUBCATEGORY,
  payload: e,
});

// Manufacturer images action creators
export const fetchManufacturerImagesRequest = () => ({
  type: FETCH_MANUFACTURER_IMAGES_REQUEST,
});

export const fetchManufacturerImagesSuccess = images => ({
  type: FETCH_MANUFACTURER_IMAGES_SUCCESS,
  payload: images,
});

export const fetchManufacturerImagesError = error => ({
  type: FETCH_MANUFACTURER_IMAGES_ERROR,
  payload: error,
});

// ================ Thunks ================ //

export const queryUserListings = (userId, config, search = '', type, memberId) => (
  dispatch,
  getState,
  sdk
) => {
  let state = getState();
  let { domLoaded = false } = state?.ui || {};

  dispatch(queryListingsRequest(userId));

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  // const variantPrefix = 'square-small';

  let queryParams = {
    author_id: userId,
    include: ['author', 'author.profileImage', 'images'],
    'fields.user': [
      'profile.displayName',
      'profile.abbreviatedName',
      'profile.profileImage',
      'profile.publicData',
    ],
    'fields.image': [
      'variants.scaled-small',
      'variants.scaled-medium',
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
      'variants.square-small',
      'variants.square-small2x',
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    'limit.images': 1,
  };

  const filter = hasQueryParam({ search }, 'pub_category', true);
  const filterSubcategory = hasQueryParam({ search }, 'pub_subcategory', true);

  dispatch(setCategories(STORE_SIDEBAR_CATEGORIES));

  let currentCategory = 'all';
  if (filter && filter !== 'all') {
    queryParams['pub_category'] = filter;
    currentCategory = filter;
  }
  dispatch(setCurrentCategory(currentCategory));

  // Keep seller-embedded subcategory filters aligned with the taxonomy helper
  // for the active category instead of hosted listingFields enums.
  const filterSubcategories = getSubcategoriesForCategory(currentCategory);
  dispatch(setSubcategories(filterSubcategories));

  let currentSubcategory = 'all';
  if (filterSubcategory) {
    const normalizedSubcategory = normalizeLegacySubcategorySlug(filterSubcategory);
    let inOptions = filterSubcategories.find(fs => fs.option === normalizedSubcategory);
    if (inOptions) {
      if (normalizedSubcategory !== 'all') {
        queryParams['pub_subcategory'] = normalizedSubcategory;
      }
      currentSubcategory = normalizedSubcategory;
    }
  }
  dispatch(setCurrentSubcategory(currentSubcategory));

  const mappedQueryParams = mapTaxonomySearchParamsToSdkQuery(queryParams);

  return sdk.listings
    .query(mappedQueryParams)
    .then(response => {
      const creatorsId = (response?.data?.data || [])
        .map(l => l?.attributes?.publicData?.creatorId)
        .filter(l => l);
      if (domLoaded) {
        dispatch(fetchCreators(creatorsId, true));
        dispatch(fetchStoresWithReviews());
      }
      // Pick only the id and type properties from the response listings
      let storeType = type == 'detail' ? 'store' : type == 'owner' ? 'owner' : 'associated';
      response.data.data = filterCompletedListings(response?.data?.data || []).filter(l => {
        let { creatorType, creatorId } = l?.attributes?.publicData;
        if (storeType === 'owner') {
          // return creatorType === storeType;
        }

        if (storeType === 'associated') {
          return creatorType == 'associated' && memberId && creatorId == memberId;
        }

        return true;
      });
      const listingRefs = response.data.data.map(({ id, type }) => ({ id, type }));
      dispatch(addMarketplaceEntities(response));
      dispatch(queryListingsSuccess(listingRefs));

      // Fetch manufacturer images if needed
      dispatch(fetchManufacturerImages(response.data.data, config));

      return response;
    })
    .catch(e => dispatch(queryListingsError(storableError(e))));
};

export const queryUserReviews = userId => (dispatch, getState, sdk) => {
  sdk.reviews
    .query({
      subject_id: userId,
      state: 'public',
      include: ['author', 'author.profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    })
    .then(response => {
      const reviews = denormalisedResponseEntities(response);
      dispatch(queryReviewsSuccess(reviews));
    })
    .catch(e => dispatch(queryReviewsError(e)));
};

export const fetchManufacturerImages = (listings, config) => async (dispatch, getState, sdk) => {
  try {
    dispatch(fetchManufacturerImagesRequest());

    // Filter listings that are from manufacturers (regardless of whether they have images)
    const manufacturerListings = listings.filter(listing => {
      const { isFromManufacturer, manufacturerListingId } = listing?.attributes?.publicData || {};
      return isFromManufacturer && manufacturerListingId;
    });

    if (manufacturerListings.length === 0) {
      dispatch(fetchManufacturerImagesSuccess({}));
      return;
    }

    // Get the original listing IDs
    const originalListingIds = manufacturerListings.map(
      listing => listing.attributes.publicData.manufacturerListingId
    );

    // Fetch the original listings to get their images
    const response = await sdk.listings.query({
      ids: originalListingIds,
      include: ['images'],
      'fields.image': [
        'variants.scaled-small',
        'variants.scaled-medium',
        'variants.listing-card',
        'variants.listing-card-2x',
      ],
      'limit.images': 1,
    });

    // Add the images to the marketplace entities so they can be accessed
    dispatch(addMarketplaceEntities(response));

    // Create a map of manufacturer listing ID to original listing images
    const imagesMap = {};
    response.data.data.forEach(originalListing => {
      // Get images from relationships
      const imageRefs = originalListing.relationships?.images?.data || [];

      if (imageRefs.length > 0) {
        // Get the actual image entities from the state
        const state = getState();
        const images = getMarketplaceEntities(state, imageRefs);

        if (images.length > 0) {
          imagesMap[originalListing.id.uuid] = images;
        }
      }
    });
    dispatch(fetchManufacturerImagesSuccess(imagesMap));
  } catch (error) {
    console.error('Error fetching manufacturer images:', error);
    dispatch(fetchManufacturerImagesError(storableError(error)));
  }
};

export const showUser = userId => (dispatch, getState, sdk) => {
  dispatch(showUserRequest(userId));
  return sdk.users
    .show({
      id: userId,
      include: ['profileImage'],
      // `profile.bio` feeds the About section rendered below the contact block.
      'fields.user': [
        'profile.publicData',
        'profile.displayName',
        'profile.abbreviatedName',
        'profile.metadata',
        'profile.bio',
      ],
      'fields.image': ['variants.square-small', 'variants.square-small2x', 'variants.default'],
    })
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(showUserSuccess(response?.data?.data?.attributes?.profile?.bio ?? null));
      return response;
    })
    .catch(e => dispatch(showUserError(storableError(e))));
};

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  const userId = new UUID(params.id);

  // Clear state so that previously loaded data is not visible
  // in case this page load fails.
  dispatch(setInitialState());

  return Promise.all([
    dispatch(showUser(userId)),
    dispatch(queryUserReviews(userId)),
  ])
    .then(([userResponse]) => {
      // Check if the seller is approved
      const user = userResponse?.data?.data;
      const publicData = user?.attributes?.profile?.publicData || {};
      const isApprovedSeller = publicData.approved_seller === true;

      // If the user is a seller (provider) but not approved, block access to their store page
      const userType = publicData.userType;
      const isSeller = userType === 'provider' || userType === 'provider-customer';

      if (isSeller && !isApprovedSeller) {
        // Create a 404-like error to show NotFoundPage
        const error = new Error('Seller not approved');
        error.status = 404;
        error.statusText = 'Not Found';
        dispatch(showUserError(storableError(error)));
        throw error;
      }

      return userResponse;
    })
    .catch(error => {
      throw error;
    });
};

// ================ Selectors ================ //

export const selectManufacturerImages = state => state.TeamPage.manufacturerImages;
export const selectManufacturerImagesInProgress = state =>
  state.TeamPage.manufacturerImagesInProgress;
export const selectManufacturerImagesError = state => state.TeamPage.manufacturerImagesError;
